import { useEffect, useMemo } from 'react';
import { toViewKey, useTableFilterFavourites } from './tableFilterFavouritesContext';

/**
 * Per-user, per-table set of "hearted" filter field keys. Hearted fields are shown in the default
 * filter set when the table loads (the chip bar renders them even when empty), so the user can pin
 * the filters they use most. Persisted on the backend — see {@link useTableFilterFavourites} for the
 * store, the one-time localStorage migration and the write behaviour.
 *
 * Until the user toggles a heart, nothing is persisted and the set *follows* `defaultKeys` on every
 * render — filter configs that depend on async data (the calendar's location trees) only become
 * default-worthy once that data lands, so a set frozen at mount time would drop them for good.
 * The first toggle persists an explicit set, which then wins — including an intentionally empty one.
 */
export const useHeartedFilters = (filterName: string, defaultKeys: string[] = []) => {
  const { favouritesFor, setFavourites, ensureLoaded } = useTableFilterFavourites();
  const viewKey = useMemo(() => toViewKey(filterName), [filterName]);

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  const hearted = favouritesFor(viewKey) ?? defaultKeys;

  const isHearted = (fieldKey: string) => hearted.includes(fieldKey);

  const toggleHearted = (fieldKey: string) => {
    setFavourites(viewKey, hearted.includes(fieldKey) ? hearted.filter(k => k !== fieldKey) : [...hearted, fieldKey]);
  };

  return { hearted, isHearted, toggleHearted };
};
